import * as React from "react";
import { alpha } from "@mui/material/styles";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { svgIconClasses } from "@mui/material/SvgIcon";
import { toggleButtonClasses } from "@mui/material/ToggleButton";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { gray, brand, indigo, white } from "../themePrimitives";

export const inputsCustomisations = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        boxSizing: "border-box",
        transition: "all 100ms ease-in",
        "&:focus-visible": {
          outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          outlineOffset: "2px",
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: "none",
        borderRadius: (theme.vars || theme).shape.borderRadius,
        textTransform: "none",
        fontWeight: 600,
        letterSpacing: 0,
        transition:
          "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease",
        variants: [
          {
            props: {
              size: "small",
            },
            style: {
              height: "2.25rem",
              padding: "8px 12px",
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              height: "2.5rem", // 40px
            },
          },
          {
            props: {
              color: "primary",
              variant: "contained",
            },
            style: {
              color: indigo[300],
              backgroundColor: brand[500],
              "&:hover": {
                color: indigo[300],
                backgroundColor: brand[500],
                boxShadow: "0 10px 28px rgba(32, 30, 90, 0.12)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                color: indigo[300],
                transform: "translateY(0)",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                color: (theme.vars || theme).palette.text.secondary,
              },
            },
          },
          {
            props: {
              color: "secondary",
              variant: "contained",
            },
            style: {
              color: white[500],
              backgroundColor: indigo[200],
              border: `1px solid ${indigo[200]}`,
              "&:hover": {
                backgroundColor: indigo[300],
                boxShadow: "0 10px 28px rgba(32, 30, 90, 0.12)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor: indigo[300],
                transform: "translateY(0)",
              },
            },
          },
          {
            props: {
              variant: "outlined",
            },
            style: {
              color: indigo[200],
              border: "1px solid",
              borderColor: indigo[200],
              backgroundColor: white[500],
              "&:hover": {
                backgroundColor: alpha(indigo[200], 0.06),
                borderColor: indigo[300],
              },
              "&:active": {
                backgroundColor: alpha(brand[600], 0.1),
              },
            },
          },
          {
            props: {
              variant: "card-action",
            },
            style: {
              color: indigo[200],
              border: 0,
              borderColor: brand[600],
              backgroundColor: white[500],
              "&:hover": {
                backgroundColor: alpha(brand[600], 0.1),
              },
              "&:active": {
                backgroundColor: alpha(brand[600], 0.1),
              },
            },
          },
          {
            props: {
              color: "secondary",
              variant: "outlined",
            },
            style: {
              color: brand[700],
              border: "1px solid",
              borderColor: brand[200],
              backgroundColor: brand[50],
              "&:hover": {
                backgroundColor: brand[100],
                borderColor: brand[400],
              },
              "&:active": {
                backgroundColor: alpha(brand[200], 0.7),
              },
            },
          },
          {
            props: {
              variant: "text",
            },
            style: {
              color: indigo[200],
              "&:hover": {
                color: indigo[300],
                backgroundColor: alpha(indigo[200], 0.06),
              },
              "&:active": {
                color: brand[800],
              },
            },
          },
          {
            props: {
              color: "secondary",
              variant: "text",
            },
            style: {
              color: brand[700],
              "&:hover": {
                backgroundColor: alpha(brand[100], 0.5),
              },
              "&:active": {
                backgroundColor: alpha(brand[200], 0.7),
              },
            },
          },
        ],
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: "none",

        borderRadius: (theme.vars || theme).shape.borderRadius,
        textTransform: "none",
        fontWeight: theme.typography.fontWeightMedium,
        letterSpacing: 0,
        color: (theme.vars || theme).palette.text.primary,
        border: "1px solid ",
        borderColor: gray[200],
        backgroundColor: alpha(gray[50], 0.3),
        "&:hover": {
          backgroundColor: gray[100],
          borderColor: gray[300],
        },
        "&:active": {
          backgroundColor: gray[200],
        },
        ...theme.applyStyles("dark", {
          backgroundColor: gray[800],
          borderColor: gray[700],
          "&:hover": {
            backgroundColor: gray[900],
            borderColor: gray[600],
          },
          "&:active": {
            backgroundColor: gray[900],
          },
        }),
        variants: [
          {
            props: {
              size: "small",
            },
            style: {
              width: "2.25rem",
              height: "2.25rem",
              padding: "0.25rem",
              [`& .${svgIconClasses.root}`]: { fontSize: "1rem" },
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              width: "2.5rem",
              height: "2.5rem",
            },
          },
        ],
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: (theme.vars || theme).shape.borderRadius,
        border: `1px solid ${brand[600]}`,
        padding: "2px",
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: "12px 16px",
        textTransform: "none",

        borderRadius: (theme.vars || theme).shape.borderRadius,
        color: brand[600],
        fontWeight: 500,
        [`&.${toggleButtonClasses.selected}`]: {
          color: white[500],
          backgroundColor: brand[600],
        },
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: (
        <CheckBoxOutlineBlankRoundedIcon
          sx={{ color: "hsla(210, 0%, 0%, 0.0)" }}
        />
      ),
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,

        borderRadius: (theme.vars || theme).shape.borderRadius,
        border: "1px solid ",
        borderColor: alpha(gray[300], 0.8),
        boxShadow: "0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset",
        backgroundColor: alpha(gray[100], 0.4),
        transition: "border-color, background-color, 120ms ease-in",
        "&:hover": {
          borderColor: brand[300],
        },
        "&.Mui-focusVisible": {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          outlineOffset: "2px",
          borderColor: brand[400],
        },
        "&.Mui-checked": {
          color: theme.palette.common.white,
          backgroundColor: brand[500],
          borderColor: brand[500],
          boxShadow: `none`,
          "&:hover": {
            backgroundColor: brand[600],
          },
        },
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        border: 0,
      },
      input: {
        "&::placeholder": {
          opacity: 0.7,
          color: gray[600],
        },
        "&:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0 100px transparent inset !important",
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: 0,
        "&:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0 100px transparent inset !important",
        },
      },
      root: ({ theme }) => ({
        display: "flex",
        padding: "12px 12px",
        color: (theme.vars || theme).palette.text.primary,
        borderRadius: (theme.vars || theme).shape.borderRadius,
        backgroundColor: white[500],
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: gray[200],
        },
        transition: "border 120ms ease-in",
        "&:hover": {
          borderColor: gray[400],
        },
        [`&.${outlinedInputClasses.focused}`]: {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: indigo[200],
            borderWidth: 2,
          },
        },
        ...theme.applyStyles("dark", {
          "&:hover": {
            borderColor: gray[500],
          },
        }),
      }),
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: (theme.vars || theme).palette.grey[500],
        ...theme.applyStyles("dark", {
          color: (theme.vars || theme).palette.grey[400],
        }),
      }),
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: () => ({
        height: "100%",
      }),
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: (theme.vars || theme).shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
        "&:hover": {
          borderColor: gray[400],
        },
        [`&.Mui-focused`]: {
          borderColor: brand[600],
        },
      }),
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      input: {
        padding: "12px",
        fontSize: "0.875rem",
      },
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      slotProps: {
        clearIndicator: {
          sx: {
            border: "none",
            boxShadow: "none",
            "&:hover": {
              background: "transparent",
            },
            height: 5,
          },
        },
        popupIndicator: {
          sx: {
            border: "none",
            boxShadow: "none",
            "&:hover": { background: "transparent" },
          },
        },
      },
    },
  },
};
